import React, { useState, useEffect } from 'react';
import AddressBookService from '../services/AddressBookService';

const AddressSelector = ({ 
  selectedAddress, 
  onAddressChange, 
  placeholder = "0x1234567890123456789012345678901234567890",
  label = "Player Address",
  className = ""
}) => {
  const [addressEntries, setAddressEntries] = useState([]);
  const [inputValue, setInputValue] = useState(selectedAddress || ''); // Raw address for validation
  const [displayValue, setDisplayValue] = useState(selectedAddress || ''); // What user sees
  const [showDropdown, setShowDropdown] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [isFromAddressBook, setIsFromAddressBook] = useState(false); // Track if selected from dropdown

  useEffect(() => {
    // Load address book entries
    const loadAddressEntries = () => {
      const entries = AddressBookService.getAllEntries();
      setAddressEntries(entries);
      setFilteredEntries(entries);
    };

    loadAddressEntries();

    // Listen for localStorage changes to update when address book is modified
    const handleStorageChange = (e) => {
      if (e.key === 'gamemaker_address_book') {
        loadAddressEntries();
      }
    };

    // Listen for custom events from the same page
    const handleAddressBookUpdate = () => {
      loadAddressEntries();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('addressBookUpdated', handleAddressBookUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('addressBookUpdated', handleAddressBookUpdate);
    };
  }, []);

  useEffect(() => {
    const address = selectedAddress || '';
    setInputValue(address);
    
    if (address) {
      const displayVal = getDisplayValue(address);
      setDisplayValue(displayVal);
      setIsFromAddressBook(displayVal !== address);
    } else {
      setDisplayValue('');
      setIsFromAddressBook(false);
    }
  }, [selectedAddress, addressEntries]);

  // Filter entries based on display value
  useEffect(() => {
    if (!displayValue.trim()) {
      setFilteredEntries(addressEntries);
      return;
    }

    const searchTerm = displayValue.toLowerCase();
    const filtered = addressEntries.filter(entry => 
      entry.nickname.toLowerCase().includes(searchTerm) ||
      entry.address.toLowerCase().includes(searchTerm)
    );
    setFilteredEntries(filtered);
  }, [displayValue, addressEntries]);

  const validateAddress = (address) => {
    if (!address || address.trim() === '') {
      return '';
    }
    if (!AddressBookService.isValidAddress(address)) {
      return 'Please enter a valid wallet address (0x followed by 40 hex characters)';
    }
    return '';
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setDisplayValue(value);
    setIsFromAddressBook(false);
    
    const error = validateAddress(value);
    setValidationError(error);
    
    // Show dropdown when typing if there are filtered entries
    if (addressEntries.length > 0) {
      setShowDropdown(true);
    }
    
    // Only call onAddressChange if address is valid or empty
    if (!error) {
      onAddressChange(value);
    }
  };

  const handleAddressBookSelect = (address) => {
    setInputValue(address);
    const displayVal = getDisplayValue(address);
    setDisplayValue(displayVal);
    setIsFromAddressBook(true);
    setValidationError('');
    setShowDropdown(false);
    onAddressChange(address);
  };

  const handleClearAddress = () => {
    setInputValue('');
    setDisplayValue('');
    setIsFromAddressBook(false);
    setValidationError('');
    onAddressChange('');
  };

  const handleInputFocus = () => {
    if (addressEntries.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for clicking on options
    setTimeout(() => setShowDropdown(false), 150);
  };

  const getDisplayValue = (address) => {
    const nickname = AddressBookService.getNicknameOrAddress(address);
    if (nickname !== address && nickname !== `${address.slice(0, 6)}...${address.slice(-4)}`) {
      return `${nickname} (${address.slice(0, 6)}...${address.slice(-4)})`;
    }
    return address;
  };

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      
      <div className="relative">
        {/* Address Input */}
        <input
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            validationError ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        
        {/* Clear button */}
        {displayValue && (
          <button
            type="button"
            onClick={handleClearAddress}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        )}

        {/* Dropdown Menu */}
        {showDropdown && filteredEntries.length > 0 && (
          <div className="absolute left-0 top-full mt-1 w-full max-w-xs sm:max-w-sm md:max-w-md bg-white border border-gray-300 rounded-md shadow-lg z-50">
            <div className="p-2 border-b border-gray-200">
              <div className="text-sm font-medium text-gray-700">
                Address Book {filteredEntries.length !== addressEntries.length && 
                `(${filteredEntries.length} of ${addressEntries.length})`}
              </div>
            </div>
            <div className="max-h-[40vh] overflow-y-auto">
              {filteredEntries.map((entry) => (
                <button
                  key={entry.address}
                  type="button"
                  onClick={() => handleAddressBookSelect(entry.address)}
                  className="w-full px-3 py-2 text-left bg-white hover:bg-blue-50 focus:outline-none focus:bg-blue-50"
                >
                  <div className="font-medium text-gray-800">{entry.nickname}</div>
                  <div className="text-sm text-gray-600 font-mono">
                    {entry.address.slice(0, 6)}...{entry.address.slice(-4)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Validation Error */}
      {validationError && (
        <div className="mt-1 text-sm text-red-600">
          {validationError}
        </div>
      )}

      {/* Selected Address Display */}
      {inputValue && !validationError && (
        <div className="mt-1 text-sm text-gray-600">
          Selected: {displayValue}
        </div>
      )}

      {/* Address Book Empty State */}
      {addressEntries.length === 0 && (
        <div className="mt-1 text-xs text-gray-500">
          No addresses in your address book. Use the address book icon (📒) in the top-right corner to add some.
        </div>
      )}
    </div>
  );
};

export default AddressSelector;